import {Badge, createStyles} from '@mantine/core';
import type {MantineColor, MantineSize} from '@mantine/core';
import React from 'react';

type StatusPillSlot = 'inner' | 'leftSection' | 'root';

interface StatusPillProps {
  children: React.ReactNode;
  // Extra per-slot class names, merged after the shared shell styles so callers can tune size
  // (e.g. shrink the font below the nucleus `xs` floor) without losing the anti-clip overrides.
  classNames?: Partial<Record<StatusPillSlot, string>>;
  color: MantineColor;
  // Optional leading glyph (a status dot, an icon, etc.).
  leftSection?: React.ReactNode;
  size?: MantineSize;
}

// Shared shell for model-hub status pills (job status, input validation, ...). Owns the overrides
// that keep the nucleus Badge from clipping its content: the nucleus theme pins Badge `root` to a
// fixed height with `overflow: hidden`, which crops ascenders/descenders and a `leftSection` icon at
// tight sizes. `height: auto` + `inner: overflow visible` let the content breathe without changing
// every other Badge in the app. Callers set their own `minHeight`/font via `classNames` (see below).
//
// Forwards its ref (and any Tooltip-injected event/aria props via `...others`) straight to the
// Badge so a caller can wrap the pill in a Mantine Tooltip — Tooltip needs a ref on its child and
// a plain function component can't take one.
const StatusPill = React.forwardRef<HTMLDivElement, StatusPillProps>(
  ({children, classNames, color, leftSection, size = 'sm', ...others}, ref): JSX.Element => {
    const {classes, cx} = useStyles();

    return (
      <Badge
        {...others}
        ref={ref}
        classNames={{
          inner: cx(classes.inner, classNames?.inner),
          leftSection: cx(classes.leftSection, classNames?.leftSection),
          root: cx(classes.root, classNames?.root),
        }}
        color={color}
        leftSection={leftSection}
        size={size}
        tt="none"
        variant="light"
      >
        {children}
      </Badge>
    );
  }
);

export default StatusPill;

const useStyles = createStyles(() => ({
  inner: {
    overflow: 'visible',
  },
  leftSection: {
    marginRight: 2,
  },
  root: {
    height: 'auto',
  },
}));
